/**
 * urlScrub.ts — keeps one-time sign-in values out of browser analytics.
 *
 * A service's consent page sends the browser back to
 * /connectors/callback?code=…&state=… (bring-your-own connectors). The page
 * removes them from the address bar on mount, but PostHog's first pageview
 * and Customer.io's page event can read the address before that. Everything
 * that records a URL runs it through here: PostHog (before_send, App.tsx),
 * Customer.io (clientMessaging.ts). Sentry has its own pattern in
 * sentryScrub.ts.
 */
import type { CaptureResult } from 'posthog-js';

const ONE_TIME_PARAMS = ['code', 'state'];

/** The URL (absolute, relative or a bare "?query") without code/state. */
export function stripOneTimeParams<T>(url: T): T {
  if (typeof url !== 'string') return url;
  const q = url.indexOf('?');
  if (q === -1) return url;
  const hashAt = url.indexOf('#', q);
  const query = hashAt === -1 ? url.slice(q + 1) : url.slice(q + 1, hashAt);
  const hash = hashAt === -1 ? '' : url.slice(hashAt);
  const pairs = query.split('&');
  const kept = pairs.filter((pair) => ONE_TIME_PARAMS.indexOf(pair.split('=')[0]) === -1);
  if (kept.length === pairs.length) return url;
  const rest = kept.filter((pair) => pair !== '');
  return `${url.slice(0, q)}${rest.length ? `?${rest.join('&')}` : ''}${hash}` as unknown as T;
}

function scrubValue(value: unknown, depth: number): unknown {
  if (typeof value === 'string') return stripOneTimeParams(value);
  if (depth > 4 || !value || typeof value !== 'object') return value;
  if (Array.isArray(value)) return value.map((v) => scrubValue(v, depth + 1));
  const out: Record<string, unknown> = {};
  Object.keys(value as Record<string, unknown>).forEach((k) => {
    out[k] = scrubValue((value as Record<string, unknown>)[k], depth + 1);
  });
  return out;
}

/** PostHog before_send: every string property loses code/state. */
export function scrubPosthogEvent(event: CaptureResult | null): CaptureResult | null {
  if (!event) return event;
  const next: CaptureResult = { ...event };
  if (event.properties) next.properties = scrubValue(event.properties, 0) as CaptureResult['properties'];
  if (event.$set) next.$set = scrubValue(event.$set, 0) as CaptureResult['$set'];
  if (event.$set_once) next.$set_once = scrubValue(event.$set_once, 0) as CaptureResult['$set_once'];
  return next;
}
